/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IButtonProps, IButtonStyles, IconButton } from '@fluentui/react/lib/Button';
import { Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { HighContrastSelector } from '@fluentui/style-utilities';
import { Range } from 'rc-slider';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import styled from 'styled-components';

type AudioPlayerProps = { audioSrc: string; onPlay?: () => void; onPause?: () => void };

const messages = defineMessages({
    playIconAriaLabel: { id: 'core.audioPlayer.playIconAriaLabel', defaultMessage: 'Play audio' },
    pauseIconAriaLabel: { id: 'core.audioPlayer.pauseIconAriaLabel', defaultMessage: 'Pause audio' },
    playTrackerAriaLabel: { id: 'core.audioPlayer.playTrackerAriaLabel', defaultMessage: 'Audio player tracker' }
});

const theme = getScopedTheme('AudioPlayer');
const playButtonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({
    root: { width: '40px', height: '40px', borderRadius: '50%' },
    icon: { fontSize: '24px', lineHeight: '24px', height: 'auto' }
});
const AudioSlider = styled(Range)`
    .rc-slider {
        border: none;
    }

    .rc-slider-handle-2 {
        display: none;
    }

    .rc-slider-track {
        width: 100%;
        background-color: ${theme.graySliderColor};
        ${HighContrastSelector} {
            border: 1px solid;
        }
    }

    .rc-slider-track-1 {
        width: 100% !important;
    }

    .rc-slider-rail {
        width: 99%;
        z-index: -1;
        background-color: ${theme.mainSliderColor};
        ${HighContrastSelector} {
            border: 1px dashed;
        }
    }

    .rc-slider-handle {
        &:hover,
        &:active,
        &:focus {
            box-shadow: none !important;
            border: solid 2px ${theme.mainSliderColor} !important;
        }

        &:focus {
            border-width: 4px !important, ${HighContrastSelector}: {
                border: 7px solid !important;
            }
        }
    }
`;

const getAudioLength = (duration: number) => {
    if (!duration) {
        return '00:00';
    }

    const roundedDuration = Math.round(duration);

    const seconds = roundedDuration % 60;
    const minutes = Math.floor(roundedDuration / 60);

    return `${minutes < 10 ? '0' : ''}${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
};

export const AudioPlayer = (props: AudioPlayerProps) => {
    const { audioSrc, onPause, onPlay } = props;
    const { localize } = useLocalization();

    const [played, setPlayed] = React.useState(0);
    const [isPlaying, setIsPlaying] = React.useState(false);
    const [playingInterval, setPlayingInterval] = React.useState<ReturnType<typeof setInterval>>();

    const audioRef = React.useRef<HTMLAudioElement>();

    const handlePlayClick = () => {
        if (isPlaying) {
            audioRef.current?.pause();
            onPause?.();
        } else {
            audioRef.current?.play();
            onPlay?.();
        }
    };

    const onPlayTriggered = () => {
        setIsPlaying(true);
        setPlayingInterval(
            setInterval(() => {
                setPlayed(audioRef.current?.currentTime);
            }, 500)
        );
    };

    const onPauseOrEndTriggered = () => {
        setIsPlaying(false);
        clearInterval(playingInterval);
        setPlayingInterval(null);
    };

    return (
        <Stack grow styles={{ root: { overflow: 'hidden' } }} horizontal verticalAlign="start" tokens={{ childrenGap: 12 }}>
            <IconButton
                styles={playButtonStyles}
                onClick={handlePlayClick}
                iconProps={{ iconName: isPlaying ? 'CirclePause' : 'Play' }}
                ariaLabel={localize(isPlaying ? messages.pauseIconAriaLabel : messages.playIconAriaLabel)}
            />

            <Stack grow tokens={{ childrenGap: 5, padding: '13px 0 0 0' }}>
                <AudioSlider
                    min={0}
                    max={audioRef.current?.duration}
                    value={[played, audioRef.current?.duration]}
                    ariaLabelGroupForHandles={[localize(messages.playTrackerAriaLabel)]}
                    onChange={([currentTime]: number[]) => {
                        audioRef.current.currentTime = currentTime;
                        if (!isPlaying) {
                            setPlayed(currentTime);
                        }
                    }}
                />
                <Stack horizontal horizontalAlign="space-between">
                    <LuisText>{getAudioLength(played)}</LuisText>
                    <LuisText>{getAudioLength(audioRef.current?.duration)}</LuisText>
                </Stack>
            </Stack>
            <audio ref={audioRef} src={audioSrc} onPlay={onPlayTriggered} onPause={onPauseOrEndTriggered} onEnded={onPauseOrEndTriggered}>
                <track kind="captions" />
            </audio>
        </Stack>
    );
};
